import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  TransactionsActiveChips,
  TransactionsFiltersPopover,
} from '../components/transactions-filters'
import type { TransactionsFilters } from '../components/transactions-filters'
import type {
  TransactionDetail,
  TransactionRow,
  TransactionsListResponse,
} from '@/api/types'
import { apiRequest } from '@/api/client'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Input } from '@/components/ui/input'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { downloadAuthenticated } from '@/shared/lib/download'

/** Admin invoice metadata of an online order (CHG-2026-08-online-order-fiscalization). */
type AdminInvoiceMetadata = {
  orderId: string
  invoiceNumber: string
  businessDate: string
  currency: string
  totalGrossCents: number
  totalTaxCents: number
  vorOrtTotalCents: number
  fiscalReceiptNumber: string | null
  fiscalSigned: boolean | null
  createdAt: string
}

type MissingFiscalReceiptRow = {
  orderId: string
  restaurantId: number
  invoiceNumber: string
  vorOrtTotalCents: number
  createdAt: string
  fiscalSigned: boolean | null
}

/** PDF needs the raw bytes, which apiRequest (JSON-only) cannot deliver. */
async function downloadInvoicePdf(orderId: string, invoiceNumber: string) {
  await downloadAuthenticated(
    `/api/v1/admin/invoices/by-order/${orderId}/pdf`,
    `Rechnung-${invoiceNumber}.pdf`,
  )
}

function centsToCurrency(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(cents / 100)
  } catch {
    return (cents / 100).toFixed(2) + ' ' + currency
  }
}

export function TransactionsPage() {
  const { t: tTransactions } = useTranslation('transactions')
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const restaurantNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const restaurant of restaurants) {
      map.set(restaurant.id, restaurant.name)
    }
    return map
  }, [restaurants])
  const [restaurantId, setRestaurantId] = useState<number | null>(null)
  const [filters, setFilters] = useState<TransactionsFilters>({
    channel: 'all',
    status: 'all',
    payment: 'all',
    range: 'all',
  })
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<TransactionRow | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!restaurantId && restaurants.length > 0) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurants, restaurantId])

  const now = new Date()
  const rangeFromTo = useMemo(() => {
    if (filters.range === 'today') {
      const from = new Date()
      from.setHours(0, 0, 0, 0)
      return { from: from.toISOString(), to: now.toISOString() }
    }
    if (filters.range === '24h') {
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      return { from: from.toISOString(), to: now.toISOString() }
    }
    if (filters.range === '7d') {
      const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      return { from: from.toISOString(), to: now.toISOString() }
    }
    return {
      from: undefined as string | undefined,
      to: undefined as string | undefined,
    }
  }, [filters.range])

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set('channel', filters.channel ?? 'all')
    params.set('limit', String(limit))
    params.set('offset', String(offset))
    if (restaurantId) params.set('restaurantId', String(restaurantId))
    if (filters.status && filters.status !== 'all')
      params.set('status', filters.status)
    if (filters.payment && filters.payment !== 'all')
      params.set('payment', filters.payment)
    if (rangeFromTo.from) params.set('from', rangeFromTo.from)
    if (rangeFromTo.to) params.set('to', rangeFromTo.to)
    if (filters.fromDate || filters.toDate) {
      // custom range takes precedence
      const iso = (d?: string, t?: string) =>
        d
          ? `${d}T${t && /^\d{2}:\d{2}(:\d{2})?$/.test(t) ? (t.length === 5 ? `${t}:00` : t) : '00:00:00'}`
          : undefined
      const fromISO = iso(filters.fromDate, filters.fromTime)
      const toISO = iso(filters.toDate, filters.toTime)
      if (fromISO) params.set('from', fromISO)
      if (toISO) params.set('to', toISO)
    }
    if (search.trim()) params.set('q', search.trim())
    return params.toString()
  }, [filters, restaurantId, limit, offset, rangeFromTo, search])

  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', queryString],
    queryFn: async () =>
      apiRequest<TransactionsListResponse>(
        `/api/v0/admin/transactions?${queryString}`,
      ),
    staleTime: 5_000,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const summary = data?.summary

  const canPrev = offset > 0
  const canNext = offset + limit < total

  const summaryByPayment = useMemo(
    () => summary?.byPaymentMethod ?? [],
    [summary],
  )

  return (
    <PermissionGate action={ACTIONS.TRANSACTION_VIEW}>
      <div className="space-y-4">
        {error && (
          <div className="text-destructive">{tTransactions('page.error')}</div>
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-1 text-sm"
              value={restaurantId ?? ''}
              onChange={(e) => {
                setOffset(0)
                setRestaurantId(Number(e.target.value))
              }}
            >
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <Input
              className="w-64"
              placeholder={tTransactions('page.searchPlaceholder')}
              value={search}
              onChange={(e) => {
                setOffset(0)
                setSearch(e.target.value)
              }}
            />
            <TransactionsFiltersPopover
              value={filters}
              onChange={(v) => {
                setOffset(0)
                setFilters(v)
              }}
              onClear={() => {
                setOffset(0)
                setFilters({
                  channel: 'all',
                  status: 'all',
                  payment: 'all',
                  range: 'all',
                })
              }}
            />
          </div>
          <div>
            <a
              href={`/api/v0/admin/transactions/export.csv?${(() => {
                const p = new URLSearchParams(queryString)
                p.set('limit', '1000')
                p.delete('offset')
                return p.toString()
              })()}`}
              className="text-sm underline"
              target="_blank"
              rel="noreferrer"
            >
              {tTransactions('page.exportCsv')}
            </a>
          </div>
        </div>

        <TransactionsActiveChips
          restaurantName={restaurants.find((r) => r.id === restaurantId)?.name}
          channel={filters.channel}
          status={filters.status}
          payment={filters.payment}
          range={filters.range}
          onClear={() => {
            setOffset(0)
            setFilters({
              channel: 'all',
              status: 'all',
              payment: 'all',
              range: 'all',
            })
          }}
          onClearField={(f) => {
            setOffset(0)
            setFilters({
              ...filters,
              [f]:
                f === 'channel' ||
                f === 'status' ||
                f === 'payment' ||
                f === 'range'
                  ? 'all'
                  : undefined,
            } as any)
          }}
        />

        {summary && (
          <div className="text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
            <div>
              {tTransactions('page.summaryTotal')}:{' '}
              <span className="font-medium">{summary.count}</span>
            </div>
            <div>
              {tTransactions('page.summaryGross')}:{' '}
              <span className="font-medium">
                {centsToCurrency(
                  summary.grossTotalCents,
                  items[0]?.currency || 'EUR',
                )}
              </span>
            </div>
            {summaryByPayment.map((pm) => (
              <div key={pm.paymentMethod || 'unknown'}>
                {pm.paymentMethod || tTransactions('page.summaryUnknown')}:{' '}
                {pm.count} /{' '}
                {centsToCurrency(
                  pm.totalGrossCents,
                  items[0]?.currency || 'EUR',
                )}
              </div>
            ))}
          </div>
        )}

        <MissingFiscalReceiptsCard restaurantNameById={restaurantNameById} />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tTransactions('page.table.channel')}</TableHead>
              <TableHead>{tTransactions('page.table.time')}</TableHead>
              <TableHead>{tTransactions('page.table.gross')}</TableHead>
              <TableHead>{tTransactions('page.table.payment')}</TableHead>
              <TableHead>{tTransactions('page.table.kiosk')}</TableHead>
              <TableHead>{tTransactions('page.table.status')}</TableHead>
              <TableHead>{tTransactions('page.table.receipt')}</TableHead>
              <TableHead>{tTransactions('page.table.id')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell>
                    <div className="h-4 w-16 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-40 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-64 bg-muted rounded" />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading &&
              items.map((t: TransactionRow) => (
                <TableRow
                  key={`${t.channel}:${t.id}`}
                  className="cursor-pointer"
                  onClick={() => setSelected(t)}
                >
                  <TableCell>
                    <Badge
                      variant={t.channel === 'pos' ? 'default' : 'secondary'}
                    >
                      {tTransactions(`values.channel.${t.channel}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(t.occurredAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {centsToCurrency(t.totalGrossCents, t.currency)}
                  </TableCell>
                  <TableCell>
                    {t.paymentMethod
                      ? tTransactions(`values.payment.${t.paymentMethod}`, {
                          defaultValue: t.paymentMethod,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>{t.stationId || '-'}</TableCell>
                  <TableCell>
                    {tTransactions(`values.status.${t.status}`, {
                      defaultValue: t.status,
                    })}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.receiptNo || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 underline decoration-dotted"
                      title={tTransactions('drawer.copy')}
                      onClick={(e) => {
                        e.stopPropagation()
                        navigator.clipboard
                          .writeText(String(t.id))
                          .then(() =>
                            toast.success(
                              tTransactions('drawer.copyIdSuccess'),
                            ),
                          )
                          .catch(() =>
                            toast.message(
                              tTransactions('drawer.copyFailedTitle'),
                              {
                                description: tTransactions(
                                  'drawer.copyFailedDescription',
                                ),
                              },
                            ),
                          )
                      }}
                    >
                      {t.id}
                      <Copy className="w-3 h-3" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-10"
                >
                  {tTransactions('page.table.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-muted-foreground">
            {offset + 1}–{Math.min(offset + limit, total)}{' '}
            {tTransactions('page.pagination.of')} {total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!canPrev}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              {tTransactions('page.pagination.prev')}
            </Button>
            <Button
              variant="outline"
              disabled={!canNext}
              onClick={() => setOffset(offset + limit)}
            >
              {tTransactions('page.pagination.next')}
            </Button>
            <label className="text-sm">
              {tTransactions('page.pagination.pageSize')}
            </label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={limit}
              onChange={(e) => {
                setOffset(0)
                setLimit(parseInt(e.target.value, 10))
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <TransactionDrawer
          selected={selected}
          onClose={() => setSelected(null)}
          restaurantNameById={restaurantNameById}
        />
      </div>
    </PermissionGate>
  )
}

/**
 * Reconciliation list (CHG-2026-08-online-order-fiscalization, D10): settled online orders with
 * vor-Ort payment parts whose RKSV beleg is missing or still unsigned past the threshold. Renders
 * nothing in steady state — an empty list is the expected, healthy answer.
 */
function MissingFiscalReceiptsCard({
  restaurantNameById,
}: {
  restaurantNameById: Map<number, string>
}) {
  const { t: tTransactions } = useTranslation('transactions')
  const { data } = useQuery({
    queryKey: ['invoices-missing-fiscal-receipts'],
    refetchInterval: 5 * 60_000,
    queryFn: async () =>
      apiRequest<Array<MissingFiscalReceiptRow>>(
        '/api/v1/admin/invoices/missing-fiscal-receipts?olderThanHours=24',
      ),
  })
  if (!data || data.length === 0) return null
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
      <h3 className="font-medium text-destructive">
        {tTransactions('invoice.reconciliationTitle')}
      </h3>
      <p className="mb-2 text-sm text-muted-foreground">
        {tTransactions('invoice.reconciliationHint')}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {tTransactions('invoice.reconciliationInvoice')}
            </TableHead>
            <TableHead>
              {tTransactions('invoice.reconciliationOrder')}
            </TableHead>
            <TableHead>{tTransactions('drawer.restaurant')}</TableHead>
            <TableHead>
              {tTransactions('invoice.reconciliationAmount')}
            </TableHead>
            <TableHead>
              {tTransactions('invoice.reconciliationSince')}
            </TableHead>
            <TableHead>
              {tTransactions('invoice.reconciliationState')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.orderId}>
              <TableCell className="font-mono text-xs">
                {row.invoiceNumber}
              </TableCell>
              <TableCell className="font-mono text-xs">{row.orderId}</TableCell>
              <TableCell>
                {restaurantNameById.get(row.restaurantId) ?? row.restaurantId}
              </TableCell>
              <TableCell>
                {centsToCurrency(row.vorOrtTotalCents, 'EUR')}
              </TableCell>
              <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
              <TableCell>
                {row.fiscalSigned === false
                  ? tTransactions('invoice.reconciliationOffline')
                  : tTransactions('invoice.reconciliationUnreported')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function TransactionDrawer({
  selected,
  onClose,
  restaurantNameById,
}: {
  selected: TransactionRow | null
  onClose: () => void
  restaurantNameById: Map<number, string>
}) {
  const { t: tTransactions } = useTranslation('transactions')
  const open = !!selected
  const { data, isLoading, error } = useQuery({
    queryKey: ['transaction-detail', selected?.channel, selected?.id],
    enabled: open && !!selected,
    queryFn: async () =>
      apiRequest<TransactionDetail>(
        `/api/v0/admin/transactions/${selected!.channel}/${selected!.id}`,
      ),
  })
  // Online orders carry an invoice once settled; 404 (not settled yet) renders as "none", which
  // is information rather than an error for support.
  const isOnline = selected?.channel === 'mobile'
  const invoiceQuery = useQuery({
    queryKey: ['transaction-invoice', selected?.id],
    enabled: open && isOnline && !!selected,
    retry: false,
    queryFn: async () =>
      apiRequest<AdminInvoiceMetadata>(
        `/api/v1/admin/invoices/by-order/${selected!.id}`,
      ),
  })
  const restaurantName = data
    ? restaurantNameById.get(data.restaurantId)
    : undefined
  const receiptNo = data?.receiptNo?.trim()

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{tTransactions('drawer.title')}</DrawerTitle>
          <DrawerDescription>
            {selected?.channel
              ? tTransactions(`values.channel.${selected.channel}`, {
                  defaultValue: selected.channel,
                })
              : ''}{' '}
            — {selected?.id}
          </DrawerDescription>
        </DrawerHeader>
        <div className="p-4 space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              <span>{tTransactions('drawer.loading')}</span>
            </div>
          )}
          {error && (
            <div className="text-destructive">
              {tTransactions('drawer.loadFailed')}
            </div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="col-span-2">
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.id')}:
                  </span>{' '}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-mono text-xs underline decoration-dotted"
                    onClick={() => {
                      if (data.id) {
                        navigator.clipboard
                          .writeText(String(data.id))
                          .then(() =>
                            toast.success(
                              tTransactions('drawer.copyIdSuccess'),
                            ),
                          )
                          .catch(() =>
                            toast.message(
                              tTransactions('drawer.copyFailedTitle'),
                              {
                                description: tTransactions(
                                  'drawer.copyFailedDescription',
                                ),
                              },
                            ),
                          )
                      }
                    }}
                    title={tTransactions('drawer.copy')}
                  >
                    {data.id}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.time')}:
                  </span>{' '}
                  {new Date(data.occurredAt).toLocaleString()}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.restaurant')}:
                  </span>{' '}
                  {restaurantName ?? data.restaurantId}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.gross')}:
                  </span>{' '}
                  {centsToCurrency(data.totalGrossCents, data.currency)}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.status')}:
                  </span>{' '}
                  {tTransactions(`values.status.${data.status}`, {
                    defaultValue: data.status,
                  })}
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.payment')}:
                  </span>{' '}
                  {data.paymentMethod
                    ? tTransactions(`values.payment.${data.paymentMethod}`, {
                        defaultValue: data.paymentMethod,
                      })
                    : '-'}
                </div>
                {data.stationId && (
                  <div>
                    <span className="text-muted-foreground">
                      {tTransactions('drawer.kiosk')}:
                    </span>{' '}
                    {data.stationId}
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">
                    {tTransactions('drawer.receipt')}:
                  </span>{' '}
                  {receiptNo ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-mono text-xs underline decoration-dotted"
                      onClick={() => {
                        navigator.clipboard
                          .writeText(receiptNo)
                          .then(() =>
                            toast.success(
                              tTransactions('drawer.copyReceiptSuccess'),
                            ),
                          )
                          .catch(() =>
                            toast.message(
                              tTransactions('drawer.copyFailedTitle'),
                              {
                                description: tTransactions(
                                  'drawer.copyFailedDescription',
                                ),
                              },
                            ),
                          )
                      }}
                      title={tTransactions('drawer.copy')}
                    >
                      {receiptNo}
                      <Copy className="w-3 h-3" />
                    </button>
                  ) : (
                    <span className="font-mono text-xs">-</span>
                  )}
                </div>
                {data.operatorId && (
                  <div>
                    <span className="text-muted-foreground">
                      {tTransactions('drawer.operator')}:
                    </span>{' '}
                    {data.operatorId}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <h3 className="font-medium mb-2">
                  {tTransactions('drawer.items')}
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{tTransactions('drawer.sku')}</TableHead>
                      <TableHead>{tTransactions('drawer.name')}</TableHead>
                      <TableHead>{tTransactions('drawer.qty')}</TableHead>
                      <TableHead>{tTransactions('drawer.unit')}</TableHead>
                      <TableHead>{tTransactions('drawer.line')}</TableHead>
                      <TableHead>{tTransactions('drawer.vat')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((it) => (
                      <TableRow key={it.sku + it.name}>
                        <TableCell className="font-mono text-xs">
                          {it.sku}
                        </TableCell>
                        <TableCell>{it.name}</TableCell>
                        <TableCell>{it.qty}</TableCell>
                        <TableCell>
                          {centsToCurrency(it.unitGrossCents, data.currency)}
                        </TableCell>
                        <TableCell>
                          {centsToCurrency(it.lineGrossCents, data.currency)}
                        </TableCell>
                        <TableCell>{it.vatCode || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {isOnline && (
                <div className="mt-3">
                  <h3 className="font-medium mb-2">
                    {tTransactions('invoice.title')}
                  </h3>
                  {invoiceQuery.data ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">
                          {tTransactions('invoice.number')}:
                        </span>{' '}
                        <span className="font-mono text-xs">
                          {invoiceQuery.data.invoiceNumber}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {tTransactions('invoice.vorOrt')}:
                        </span>{' '}
                        {centsToCurrency(
                          invoiceQuery.data.vorOrtTotalCents,
                          invoiceQuery.data.currency,
                        )}
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          {tTransactions('invoice.fiscal')}:
                        </span>{' '}
                        {invoiceQuery.data.vorOrtTotalCents <= 0 ? (
                          <span className="font-mono text-xs">-</span>
                        ) : invoiceQuery.data.fiscalReceiptNumber ? (
                          <span className="font-mono text-xs">
                            {invoiceQuery.data.fiscalReceiptNumber}
                            {invoiceQuery.data.fiscalSigned === false &&
                              ` (${tTransactions('invoice.fiscalPendingOffline')})`}
                          </span>
                        ) : (
                          <span className="text-destructive">
                            {tTransactions('invoice.fiscalMissing')}
                          </span>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            void downloadInvoicePdf(
                              invoiceQuery.data.orderId,
                              invoiceQuery.data.invoiceNumber,
                            ).catch(() =>
                              toast.error(tTransactions('invoice.loadFailed')),
                            )
                          }}
                        >
                          {tTransactions('invoice.downloadPdf')}
                        </Button>
                      </div>
                    </div>
                  ) : invoiceQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {tTransactions('invoice.none')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline">{tTransactions('drawer.close')}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
